import { useMemo, useState } from "react";
import {
  Stack,
  Title,
  Group,
  Paper,
  Text,
  Alert,
  Badge,
  Button,
  SegmentedControl,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { DataTable } from "../../DataTable";

export function ValidationStep({
  validationResults,
  validationRecords,
  identifierColumn,
  filenameColumn,
  onPrev,
  onNext,
}) {
  const [filter, setFilter] = useState("all");

  const filteredRecords = useMemo(() => {
    if (filter === "errors") return validationRecords.filter((r) => r.error);
    if (filter === "ready") return validationRecords.filter((r) => !r.error);
    return validationRecords;
  }, [validationRecords, filter]);

  return (
    <Stack gap="md">
      <Title order={3}>Validation Results</Title>
      <Group grow>
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderLeft: "4px solid var(--mantine-color-green-filled)",
          }}
        >
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Resolved Users
          </Text>
          <Text size="xl" fw={700}>
            {validationResults?.mappedCount}
          </Text>
        </Paper>
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderLeft: "4px solid var(--mantine-color-red-filled)",
          }}
        >
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Errors
          </Text>
          <Text size="xl" fw={700}>
            {validationResults?.unmappedCount}
          </Text>
        </Paper>
      </Group>

      {validationResults?.unmappedCount > 0 && (
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title="Validation Warnings"
          color="orange"
        >
          Some records in your manifest could not be resolved. You can proceed,
          but these documents will be skipped.
        </Alert>
      )}

      <DataTable
        columns={[
          {
            accessor: "identifier",
            render: (r) =>
              r.identifierValue ||
              r[identifierColumn?.toLowerCase().replace(/\s/g, "")] ||
              "-",
          },
          {
            accessor: "filename",
            render: (r) =>
              r.filename ||
              r[filenameColumn?.toLowerCase().replace(/\s/g, "")] ||
              "-",
          },
          {
            accessor: "status",
            filter: (
              <SegmentedControl
                value={filter}
                onChange={setFilter}
                size="xs"
                data={[
                  { label: "All", value: "all" },
                  { label: "Ready", value: "ready" },
                  { label: "Errors", value: "errors" },
                ]}
              />
            ),
            filtering: filter !== "all",
            render: (r) =>
              r.error ? (
                <Badge color="red">{r.error}</Badge>
              ) : (
                <Badge color="green">Ready</Badge>
              ),
          },
        ]}
        records={filteredRecords}
        withTableBorder={true}
        withColumnBorders={true}
        striped
        height={300}
      />

      <Group justify="space-between" mt="xl">
        <Button variant="default" onClick={onPrev}>
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={validationResults?.mappedCount === 0}
        >
          Next: Upload Documents
        </Button>
      </Group>
    </Stack>
  );
}
